/**
 * The subset of prescription fields needed to decide refill eligibility.
 *
 * Convention across PIMS converters (Bitwerx, ezyVet): `refill_count` is the
 * number of refills USED and `refill_limit` the total authorized, so refills
 * still available = `refill_limit - refill_count`. Some prescriptions only
 * expose a remaining quantity, in which case we fall back to `remaining`.
 */
export interface PrescriptionValidityInput {
    remaining?: number | null
    refill_count?: number | null
    refill_limit?: number | null
    expiration_date?: number | null
}

export interface PrescriptionValidityStatus {
    /** Whether eligibility is driven by remaining quantity vs. the refill count/limit model. */
    usesRemaining: boolean
    /** Refills still available (refill_limit - refill_count). */
    refillsLeft: number
    /** True when there is stock available to refill (refills left, or remaining quantity). */
    hasRefillsLeft: boolean
    isExpired: boolean
}

/**
 * Single source of truth for whether a synced prescription is valid.
 */
export function getPrescriptionValidityStatus(
    prescription: PrescriptionValidityInput,
    now: number = Date.now(),
): PrescriptionValidityStatus {
    const { remaining, refill_count, refill_limit, expiration_date } =
        prescription

    const usesRefills = refill_limit != null || refill_count != null
    const usesRemaining = !usesRefills && remaining != null

    const refillsLeft = (refill_limit || 0) - (refill_count || 0)

    const hasRefillsLeft = usesRemaining
        ? (remaining || 0) > 0
        : refillsLeft > 0

    const isExpired = expiration_date
        ? new Date(expiration_date).getTime() <= now
        : false

    return {
        usesRemaining,
        refillsLeft,
        hasRefillsLeft,
        isExpired,
    }
}
